/**
 * Heartbeat Analytics view — dashboard for heartbeat run history and stats.
 */

import { html, nothing } from "lit";
import { t } from "../i18n.ts";

export type HbHistoryEntry = {
  seq: number;
  ts: number;
  status: "sent" | "ok-empty" | "ok-token" | "skipped" | "failed";
  reason?: string;
  channel?: string;
  preview?: string;
  durationMs?: number;
  hasMedia?: boolean;
  silent?: boolean;
  indicatorType?: "ok" | "alert" | "error";
  accountId?: string;
};

export type HbAggregateStats = {
  total: number;
  byStatus: Record<string, number>;
  bySkipReason: Record<string, number>;
  avgDurationMs: number;
  medianDurationMs: number;
  contextHashSkips: number;
  semanticDedupSkips: number;
  urgencyGateSkips: number;
  okRate: number;
  alertRate: number;
  oldestTs: number | null;
  newestTs: number | null;
};

export type HeartbeatViewProps = {
  loading: boolean;
  error: string | null;
  history: HbHistoryEntry[];
  liveEvents: HbHistoryEntry[];
  stats: HbAggregateStats | null;
  paused: boolean;
  timeRange: "1h" | "6h" | "24h";
  onRefresh: () => void;
  onTogglePause: () => void;
  onTimeRangeChange: (range: "1h" | "6h" | "24h") => void;
};

// ─── Helpers ─────────────────────────────────────────────────

function formatPct(ratio: number): string {
  return `${Math.round(ratio * 100)}%`;
}

function formatDuration(ms: number): string {
  if (ms < 1000) return `${ms}ms`;
  return `${(ms / 1000).toFixed(1)}s`;
}

function formatTime(ts: number): string {
  const d = new Date(ts);
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${pad(d.getHours())}:${pad(d.getMinutes())}:${pad(d.getSeconds())}`;
}

function statusClass(status: string): string {
  if (status === "sent") return "hb-sent";
  if (status === "failed") return "hb-failed";
  if (status === "skipped") return "hb-skipped";
  return "hb-ok";
}

function statusColor(status: string): string {
  if (status === "sent") return "var(--ok, #34d399)";
  if (status === "failed") return "var(--danger, #ef4444)";
  if (status === "skipped") return "var(--warn, #f59e0b)";
  return "var(--muted)";
}

function statusDot(status: string): string {
  if (status === "sent") return "●";
  if (status === "failed") return "✕";
  if (status === "skipped") return "◌";
  return "○";
}

// ─── Main Render ─────────────────────────────────────────────

export function renderHeartbeat(props: HeartbeatViewProps) {
  const liveSet = new Set(props.liveEvents.map((e) => e.seq));
  const combined = [...props.liveEvents, ...props.history]
    .filter((e, i, arr) => arr.findIndex((x) => x.seq === e.seq) === i)
    .sort((a, b) => b.ts - a.ts);

  return html`
    <div>
      <div class="row" style="justify-content: space-between; align-items: center; margin-bottom: 16px; flex-wrap: wrap; gap: 12px;">
        <div>
          <h3 style="margin: 0; font-size: 18px; font-weight: 600;">${t("hb.title")}</h3>
          <div class="card-sub">${t("hb.subtitle")}</div>
        </div>
        <div class="row" style="gap: 8px; align-items: center;">
          <div class="row" style="gap: 4px;">
            ${(["1h", "6h", "24h"] as const).map(
              (r) => html`
                <button
                  class="btn btn-sm ${props.timeRange === r ? "active" : ""}"
                  @click=${() => props.onTimeRangeChange(r)}
                >
                  ${r}
                </button>
              `,
            )}
          </div>
          <button class="btn btn-sm" ?disabled=${props.loading} @click=${props.onTogglePause}>
            ${props.paused ? t("hb.resume") : t("hb.pause")}
          </button>
          <button class="btn btn-sm" ?disabled=${props.loading} @click=${props.onRefresh}>
            ${props.loading ? t("hb.loading") : t("hb.refresh")}
          </button>
        </div>
      </div>

      ${props.error ? html`<div class="callout danger" style="margin-bottom: 12px;">${props.error}</div>` : nothing}

      ${props.stats ? renderStatsCards(props.stats) : nothing}

      <div class="card" style="margin-top: 16px;">
        <div class="card-title">${t("hb.timeline")}</div>
        <div class="row" style="flex-wrap: wrap; gap: 8px; margin-top: 12px;">
          ${
            combined.length === 0
              ? html`<div class="muted" style="padding: 12px 0;">${t("hb.no_events")}</div>`
              : combined.slice(0, 24).map(
                  (e) => html`
              <div
                class="mono"
                style="
                  display: flex; flex-direction: column; align-items: center; gap: 4px;
                  color: ${statusColor(e.status)};
                "
                title=${e.reason ?? e.status}
              >
                <span style="font-size: 18px;">${statusDot(e.status)}</span>
                <span class="muted" style="font-size: 10px;">${formatTime(e.ts)}</span>
              </div>
            `,
                )
          }
        </div>
      </div>

      <div class="card" style="margin-top: 16px;">
        <div class="card-title">${t("hb.event_log")}</div>
        <div class="list" style="max-height: 400px; overflow-y: auto; margin-top: 12px;">
          ${
            combined.length === 0
              ? html`<div class="muted" style="padding: 24px; text-align: center;">${t("hb.no_events")}</div>`
              : combined.map((e) => renderEventRow(e, liveSet.has(e.seq)))
          }
        </div>
      </div>
    </div>
  `;
}

function renderStatsCards(stats: HbAggregateStats) {
  const skipped = stats.byStatus["skipped"] ?? 0;
  const failed = stats.byStatus["failed"] ?? 0;
  const apiSaved = stats.contextHashSkips + stats.semanticDedupSkips + stats.urgencyGateSkips;

  return html`
    <div class="stat-grid" style="margin-bottom: 16px;">
      <div class="stat">
        <div class="stat-label">${t("hb.stat_total")}</div>
        <div class="stat-value">${stats.total}</div>
      </div>
      <div class="stat">
        <div class="stat-label">${t("hb.stat_ok_rate")}</div>
        <div class="stat-value">${formatPct(stats.okRate)}</div>
      </div>
      <div class="stat">
        <div class="stat-label">${t("hb.stat_alert_rate")}</div>
        <div class="stat-value">${formatPct(stats.alertRate)}</div>
      </div>
      <div class="stat">
        <div class="stat-label">${t("hb.stat_avg_duration")}</div>
        <div class="stat-value mono">${formatDuration(stats.avgDurationMs)}</div>
      </div>
      <div class="stat">
        <div class="stat-label">${t("hb.stat_skipped")}</div>
        <div class="stat-value">${skipped}</div>
      </div>
      <div class="stat">
        <div class="stat-label">${t("hb.stat_failed")}</div>
        <div class="stat-value">${failed}</div>
      </div>
      <div class="stat">
        <div class="stat-label">${t("hb.stat_duplicates")}</div>
        <div class="stat-value">${stats.semanticDedupSkips}</div>
      </div>
      <div class="stat">
        <div class="stat-label">${t("hb.stat_api_saved")}</div>
        <div class="stat-value">${apiSaved}</div>
      </div>
    </div>
  `;
}

function renderEventRow(e: HbHistoryEntry, isLive: boolean) {
  return html`
    <div class="list-item">
      <div class="list-main">
        <div class="list-title row" style="gap: 8px; align-items: center;">
          <span class="mono" style="color: ${statusColor(e.status)};">${statusDot(e.status)}</span>
          ${t(`hb.status_${e.status}`)}
          ${isLive ? html`<span style="font-size: 10px; padding: 2px 6px; background: var(--accent); color: white; border-radius: 4px;">${t("hb.new")}</span>` : nothing}
        </div>
        <div class="list-sub">${e.reason ?? ""}</div>
      </div>
      <div class="list-meta">
        <span class="mono">${formatTime(e.ts)}</span>
        ${e.durationMs != null ? html`<span class="mono">${formatDuration(e.durationMs)}</span>` : nothing}
        ${e.channel ? html`<span>${e.channel}</span>` : nothing}
        ${e.preview ? html`<span style="max-width: 120px; overflow: hidden; text-overflow: ellipsis; white-space: nowrap;">${e.preview}</span>` : nothing}
      </div>
    </div>
  `;
}
